import { CheckIcon, ChevronDownIcon, MapPinIcon } from "lucide-react";
import { Suspense, useDeferredValue, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Skeleton } from "@/components/ui/skeleton";
import { useSearchStationsQuery } from "@/datasource/http/stations/query";
import type { Station } from "@/models/station";

export interface StationAutocompleteProps {
  placeholder?: string;
  value?: Station | null;
  onSelect?: (station: Station | null) => void;
  disabled?: boolean;
  error?: boolean;
}

function StationsList({
  search,
  selectedId,
  onSelect,
}: {
  search: string;
  selectedId?: number;
  onSelect: (station: Station) => void;
}) {
  const { data: stations } = useSearchStationsQuery({
    search,
    limit: 10,
  });

  if (stations.length === 0) {
    return <CommandEmpty>No stations found.</CommandEmpty>;
  }

  return (
    <CommandGroup>
      {stations.map((station) => (
        <CommandItem
          key={station.id}
          value={station.id.toString()}
          onSelect={() => onSelect(station)}
          className="flex items-center gap-2"
        >
          <MapPinIcon className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium truncate">{station.name}</span>
          {selectedId === station.id && <CheckIcon size={16} className="ml-auto" />}
        </CommandItem>
      ))}
    </CommandGroup>
  );
}

function StationsListSkeleton() {
  return (
    <CommandGroup>
      {[0, 1, 2, 3, 4].map((skeletonId) => (
        <div key={skeletonId} className="flex items-center gap-2 px-2 py-1.5">
          <Skeleton className="h-4 w-4" />
          <Skeleton className="h-4 w-32" />
        </div>
      ))}
    </CommandGroup>
  );
}

export function StationAutocomplete({
  placeholder = "Search stations...",
  value,
  onSelect,
  disabled = false,
  error = false,
}: StationAutocompleteProps) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const deferredSearch = useDeferredValue(searchQuery);

  const handleSelect = (station: Station) => {
    setOpen(false);
    onSelect?.(station);
  };

  return (
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={`w-full justify-between border-input bg-background px-3 font-normal outline-offset-0 outline-none hover:bg-background focus-visible:outline-[3px] ${
            error ? "border-destructive" : ""
          }`}
          disabled={disabled}
        >
          {value ? (
            <span className="flex min-w-0 items-center gap-2">
              <MapPinIcon className="h-4 w-4 text-muted-foreground" />
              <span className="truncate">{value.name}</span>
            </span>
          ) : (
            <span className="text-muted-foreground">{placeholder}</span>
          )}
          <ChevronDownIcon
            size={16}
            className="shrink-0 text-muted-foreground/80"
            aria-hidden="true"
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-full min-w-(--radix-popper-anchor-width) border-input p-0"
        align="start"
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={placeholder}
            value={searchQuery}
            onValueChange={setSearchQuery}
          />
          <CommandList>
            {open && (
              <Suspense fallback={<StationsListSkeleton />}>
                <StationsList
                  search={deferredSearch}
                  selectedId={value?.id}
                  onSelect={handleSelect}
                />
              </Suspense>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
